import type { BlogInteractionsData, CreateCommentInput } from "@adafia/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
	addComment,
	deleteComment,
	getComments,
	getInteractions,
	getNodeBlocks,
	getPublishedNote,
	incrementBlockLike,
	incrementLike,
	incrementView,
} from "../api/blogs";

export const useBlogPost = (id?: string) => {
	const queryClient = useQueryClient();

	const { data: post, isLoading: isLoadingPost } = useQuery({
		queryKey: ["blog", id],
		queryFn: () => getPublishedNote(id ?? ""),
		enabled: !!id,
	});

	const { data: blocks, isLoading: isLoadingBlocks } = useQuery({
		queryKey: ["blog-blocks", id],
		queryFn: () => getNodeBlocks(id ?? ""),
		enabled: !!id,
	});

	const { data: interactions, isLoading: isLoadingInteractions } =
		useQuery<BlogInteractionsData>({
			queryKey: ["blog-interactions", id],
			queryFn: () => getInteractions(id ?? ""),
			enabled: !!id,
		});

	const { data: comments, isLoading: isLoadingComments } = useQuery({
		queryKey: ["blog-comments", id],
		queryFn: () => getComments(id ?? ""),
		enabled: !!id,
	});

	// Trigger view count on mount
	useEffect(() => {
		if (id) incrementView(id);
	}, [id]);

	const likeMutation = useMutation({
		mutationFn: () => incrementLike(id ?? ""),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: ["blog-interactions", id] });
			const previous = queryClient.getQueryData(["blog-interactions", id]);
			queryClient.setQueryData<BlogInteractionsData>(
				["blog-interactions", id],
				(old: BlogInteractionsData | undefined) => {
					if (!old) return old;
					return {
						...old,
						global: {
							...old.global,
							likes: (old.global.likes || 0) + 1,
							userHasLiked: true,
						},
					};
				},
			);
			return { previous };
		},
		onError: (_err, _new, context) => {
			queryClient.setQueryData(["blog-interactions", id], context?.previous);
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: ["blog-interactions", id],
				refetchType: "none",
			});
		},
	});

	const blockLikeMutation = useMutation({
		mutationFn: (blockId: string) => incrementBlockLike(id ?? "", blockId),
		onMutate: async (blockId) => {
			await queryClient.cancelQueries({ queryKey: ["blog-interactions", id] });
			const previous = queryClient.getQueryData(["blog-interactions", id]);
			queryClient.setQueryData<BlogInteractionsData>(
				["blog-interactions", id],
				(old: BlogInteractionsData | undefined) => {
					if (!old) return old;
					return {
						...old,
						blocks: old.blocks.map((b) =>
							b.blockId === blockId
								? { ...b, likes: (b.likes || 0) + 1, userHasLiked: true }
								: b,
						),
					};
				},
			);
			return { previous };
		},
		onError: (_err, _new, context) => {
			queryClient.setQueryData(["blog-interactions", id], context?.previous);
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: ["blog-interactions", id],
				refetchType: "none",
			});
		},
	});

	const commentMutation = useMutation({
		mutationFn: (data: Omit<CreateCommentInput, "nodeId">) =>
			addComment(id ?? "", data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["blog-comments", id],
				refetchType: "all",
			});
		},
	});

	const deleteCommentMutation = useMutation({
		mutationFn: (commentId: string) => deleteComment(id ?? "", commentId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["blog-comments", id],
				refetchType: "all",
			});
		},
	});

	return {
		post,
		blocks,
		interactions,
		comments,
		isLoading:
			isLoadingPost ||
			isLoadingBlocks ||
			isLoadingInteractions ||
			isLoadingComments,
		likeMutation,
		blockLikeMutation,
		commentMutation,
		deleteCommentMutation,
	};
};
